/**
 * Shared between the Contribution plans page and the Notes page.
 *
 * Extracted rather than duplicated so a fix to the bottom sheet - its keyboard
 * clearance in particular - only has to be made once.
 */
import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { EMPTY_PLANS } from "../../utils/plansUtils";

export const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** 'YYYY-MM-DD' -> 'Sep 8, 2026' without dragging in a date library. */
export function prettyDate(key) {
  if (!key) return '';
  const [y, m, d] = key.split('-').map(Number);
  return MONTH_SHORT[m - 1] + ' ' + d + ', ' + y;
}

export function prettyShort(key) {
  if (!key) return '';
  const [, m, d] = key.split('-').map(Number);
  return MONTH_SHORT[m - 1] + ' ' + d;
}

/** Merge whatever is stored over the empty shape so old accounts don't crash. */
export function normalizePlans(plans) {
  const p = plans || {};
  return {
    ...EMPTY_PLANS,
    ...p,
    accounts: {
      rrsp: { ...EMPTY_PLANS.accounts.rrsp, ...((p.accounts || {}).rrsp || {}) },
      tfsa: { ...EMPTY_PLANS.accounts.tfsa, ...((p.accounts || {}).tfsa || {}) },
    },
    sources: p.sources || [],
    contributions: p.contributions || [],
    notes: p.notes || [],
    folders: p.folders || [],
    reminders: p.reminders || [],
    budgetSheets: p.budgetSheets || [],
    milestones: p.milestones || [],
  };
}

// Copied from BudgetHero.jsx so the two tabs cannot drift apart.
// Order: 0 Income/primary, 1 Spent, 2 Saved-Inv, 3 Leftover.
export const PLAN_TILE_PALETTES = {
  // Purple (admin-only): one lavender family for every tile.
  purple: [{ bg: '#F1E9FB', label: '#5E35A8', value: '#5E35A8' }, { bg: '#F1E9FB', label: '#5E35A8', value: '#5E35A8' }, { bg: '#F1E9FB', label: '#5E35A8', value: '#5E35A8' }, { bg: '#F1E9FB', label: '#5E35A8', value: '#5E35A8' }],
  pink: [
    { bg: 'linear-gradient(135deg, #FAE3EC 0%, #F5D5E3 100%)', label: '#C23A72', value: '#C23A72' },
    { bg: 'linear-gradient(135deg, #DBE7F5 0%, #C4D6ED 100%)', label: '#3C6693', value: '#3C6693' },
    { bg: 'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)', label: '#5C7A4A', value: '#5C7A4A' },
    { bg: 'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)', label: '#7A5FB0', value: '#7A5FB0' },
  ],

  blue: [
    { bg: 'linear-gradient(135deg, #E1E8FA 0%, #D2DEF5 100%)', label: '#3A56C2', value: '#3A56C2' },
    { bg: 'linear-gradient(135deg, #E6E6F5 0%, #D6D6ED 100%)', label: '#5B4F93', value: '#5B4F93' },
    { bg: 'linear-gradient(135deg, #DCEFF0 0%, #C9E4E6 100%)', label: '#2F7A80', value: '#2F7A80' },
    { bg: 'linear-gradient(135deg, #E4E1F5 0%, #D2CDEB 100%)', label: '#6B5FB0', value: '#6B5FB0' },
  ],

  warm: [
    { bg: 'linear-gradient(135deg, #F7E7D9 0%, #F0D8C2 100%)', label: '#B5651D', value: '#B5651D' },
    { bg: 'linear-gradient(135deg, #EDE3D5 0%, #E2D3BC 100%)', label: '#7A6A4A', value: '#7A6A4A' },
    { bg: 'linear-gradient(135deg, #E9EFD9 0%, #DCE6C1 100%)', label: '#5C7A4A', value: '#5C7A4A' },
    { bg: 'linear-gradient(135deg, #F0E3D9 0%, #E5D0C2 100%)', label: '#9A5B3C', value: '#9A5B3C' },
  ]
};

export const SHEET_SERIF = { fontFamily: "'DM Serif Display', serif", fontWeight: 400 };
export const SHEET_FIELD_LABEL = "block text-[13px] font-semibold mb-1.5";
export const SHEET_INPUT_CLS =
  "w-full rounded-2xl px-4 py-3 text-[16px] border theme-border-100 outline-none";
export const SHEET_INPUT_STYLE = {
  background: '#FCF9FB',
  color: '#1F2937',
  fontFamily: "'DM Sans', sans-serif",
};

// iOS sizes an empty date input to its content, so it renders shorter and
// narrower than a filled one sitting right above it. Pin the box instead.
export const SHEET_DATE_STYLE = {
  ...SHEET_INPUT_STYLE,
  display: 'block',
  width: '100%',
  minHeight: '50px',
  WebkitAppearance: 'none',
  appearance: 'none',
};

/**
 * A date or time input that can actually be cleared on iOS.
 *
 * As a controlled input this does not work: WebKit's picker fires the event
 * with an empty value, React re-renders, and its controlled-input write-back
 * puts the previous date straight back into the element.
 *
 * So the element is uncontrolled and kept in step by hand. The listeners are
 * native rather than React's synthetic onChange for the same reason - we want
 * whatever the element actually holds, including an empty string.
 */
export const DateField = ({
  value, onChange, className, style, clearable = false, wrapperClassName = '',
  type = 'date', ...rest
}) => {
  const ref = useRef(null);
  const cb = useRef(onChange);
  cb.current = onChange;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const handle = () => cb.current(el.value);
    el.addEventListener('change', handle);
    el.addEventListener('input', handle);
    // WKWebView dispatches nothing when its picker's own clear button is used,
    // so read the element on the way out as well. Harmless when it agrees.
    el.addEventListener('blur', handle);
    return () => {
      el.removeEventListener('change', handle);
      el.removeEventListener('input', handle);
      el.removeEventListener('blur', handle);
    };
  }, []);

  // Only writes when the two have actually drifted, so typing is never
  // interrupted - this is for the parent resetting the value, not for echoing
  // back what the user just did.
  useEffect(() => {
    const el = ref.current;
    const next = value || '';
    if (el && el.value !== next) el.value = next;
  }, [value]);

  const showClear = clearable && !!value;

  return (
    // inline-block WITH a definite width, not shrink-to-fit. Shrink-to-fit made
    // the input's own width: 100% circular, which collapsed an empty time field
    // to a slim pill. With the span at full width the input fills it, and the
    // clear button lands at the edge of the field rather than the edge of the
    // row - which is what the inline-block was for in the first place.
    <span className={'relative inline-block w-full align-top ' + wrapperClassName}>
      <input
        type={type}
        ref={ref}
        defaultValue={value || ''}
        className={className}
        // minWidth as well as width: WebKit has been ignoring width on date and
        // time inputs and sizing them to their content instead.
        style={
          showClear
            ? { ...style, minWidth: '100%', paddingRight: '44px' }
            : { ...style, minWidth: '100%' }
        }
        {...rest}
      />
      {showClear && (
        <button
          type="button"
          onClick={() => {
            const el = ref.current;
            if (el) el.value = '';
            onChange('');
          }}
          className="absolute flex items-center justify-center rounded-full"
          style={{
            right: '10px', top: '50%', transform: 'translateY(-50%)',
            width: '24px', height: '24px', background: '#EFE7EC', padding: 0,
          }}
          aria-label="Clear date"
        >
          <X className="w-3 h-3" style={{ color: '#6B7280' }} />
        </button>
      )}
    </span>
  );
};

/**
 * Bottom sheet shared by both plan editors.
 *
 * Declared at module scope on purpose. A component defined inside another
 * component is a new type on every parent render, which makes React remount it
 * and lose any state the user was midway through typing.
 *
 * The keyboard height cannot be measured here. In WKWebView with the keyboard
 * resize mode this app uses, neither window.innerHeight nor
 * visualViewport.height changes when the keyboard opens, so there is nothing
 * to read. Instead the scroll region carries a deliberately large bottom
 * padding - taller than a full numeric keypad - so the primary button can
 * always be scrolled above the keyboard, and focusing a field pulls it to the
 * middle of the sheet automatically.
 */
export const Sheet = ({ title, subtitle, onClose, track, padBottom = 420, children }) => {
  // The clearance below is only wanted while something is focused. Applying it
  // permanently meant a short form could be scrolled down into 420px of nothing.
  const [typing, setTyping] = useState(false);
  const collapse = useRef(null);

  const onFieldFocus = (e) => {
    if (collapse.current) { window.clearTimeout(collapse.current); collapse.current = null; }
    setTyping(true);
    const el = e.target;
    // Wait for the keyboard animation before scrolling, or the browser
    // measures against the pre-keyboard layout and lands short.
    window.setTimeout(() => {
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 320);
  };

  // Tapping straight from one field to another blurs before it focuses, so the
  // collapse waits and is cancelled by the next focus. Without the delay the
  // padding drops and snaps back mid-tap, taking the scroll position with it.
  const onFieldBlur = () => {
    if (collapse.current) window.clearTimeout(collapse.current);
    collapse.current = window.setTimeout(() => setTyping(false), 220);
  };

  // Idle, the sheet still has to clear the tab bar, which sits over it. The
  // safe-area inset is added separately below, so this only covers the bar.
  const TAB_BAR_CLEARANCE = 96;
  const pad = typing ? padBottom : TAB_BAR_CLEARANCE;

  return (
  <div
    className="fixed inset-0 bg-black/40 z-40 flex items-end justify-center"
    onClick={onClose}
  >
    <div
      className="w-full bg-white rounded-t-3xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-500"
      style={{
        // Sizes to its content and caps at 94vh. While typing the clearance
        // pushes it to the cap, which is what gives the button room to scroll.
        height: 'auto',
        maxHeight: 'calc(94vh - env(safe-area-inset-top, 0px))',
        marginTop: 'env(safe-area-inset-top, 0px)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-center pt-3 pb-3 flex-shrink-0">
        <div className="w-10 h-1 bg-gray-300 rounded-full" />
      </div>
      <div
        className="flex-1 overflow-y-auto overscroll-contain px-5"
        style={{ paddingBottom: 'calc(' + pad + 'px + env(safe-area-inset-bottom, 0px))' }}
        onFocusCapture={onFieldFocus}
        onBlurCapture={onFieldBlur}
      >
        <div className="flex items-start justify-between mb-4 pt-1">
          <div>
            <h3 className="m-0" style={{ ...SHEET_SERIF, fontSize: '22px', color: '#1F2937' }}>
              {title}
            </h3>
            {subtitle && <p className="m-0 mt-1 text-[12px] text-gray-500">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: track }}
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        {children}
      </div>
    </div>
  </div>
  );
};

// Which tile tokens the three note colours map to. Storing the SLOT rather
// than a hex is what lets a theme switch recolour existing notes for free.

/**
 * Delete confirmation as a centred pop-up. Used instead of "tap again" or an
 * inline Cancel / Delete pair, both of which put the real Delete where the
 * first tap just landed - two quick taps in one place deleted things.
 * Tapping outside cancels.
 */
export const ConfirmDialog = ({
  title, body, warning, confirmLabel = 'Delete', confirmColor = '#E11D48', onConfirm, onCancel,
}) => createPortal(
  <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5" onClick={onCancel}>
    <div className="absolute inset-0 bg-black/40" />
    <div
      className="relative w-full max-w-sm rounded-2xl shadow-2xl bg-white overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      role="alertdialog"
    >
      <div className="px-5 pt-5 pb-2">
        <h3 className="m-0 text-[19px]" style={{ ...SHEET_SERIF, color: '#1F2937' }}>{title}</h3>
        {body && <p className="m-0 mt-1.5 text-[13px] text-gray-500 leading-snug">{body}</p>}
        {warning && (
          <div
            className="mt-3 rounded-2xl px-4 py-3 text-[13px] leading-snug"
            style={{ background: '#FDF0E2', color: '#8A5A1E' }}
          >
            {warning}
          </div>
        )}
      </div>
      <div className="px-5 pb-5 pt-3 flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 rounded-full text-sm font-bold text-gray-500 bg-white border border-gray-200"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="flex-1 py-3 rounded-full text-sm font-bold text-white"
          style={{ background: confirmColor }}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>,
  document.body
);
